/**
 * Operator-declared evacuation-order renderer (#445-(3)).
 *
 * A PARALLEL Cesium layer to the EXTERNAL GeoBC evac layer (ArcgisFeatureLayer,
 * category 'evacuation') — it renders the operator's OWN orders
 * (operator_evacuation_orders), drawn + recorded on behalf of a named issuing
 * authority. Reuses the VIOLET protective-action palette (evacuationStyle) so the
 * two read alike, but it is a DISTINCT source/layer/prefKey (operatorEvacuationOrders)
 * and the side panel labels it "recorded in-COP" so the operator can never confuse a
 * COP-recorded order with an ingested GeoBC one.
 *
 * Imperative installer (MapView owns the viewer). Default OFF; gated to regional+
 * zoom (REGIONAL_VISIBLE_FAR_M) so the country-zoom default view never clutters;
 * nothing pulses (§3B.8 motion budget). Mirrors the ArcgisFeatureLayer polygon path.
 */
import * as Cesium from 'cesium';
import type { OperatorEvacOrderFeature, OperatorEvacOrderFeatureCollection } from '@tsr/shared-types';
import { fetchOperatorEvacuationOrders, type BboxQuery } from '../api';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';
import { evacuationStyle } from '../styles/evacuationStyles';
import { readArcgisId } from './ArcgisFeatureLayer';
import { renderDiscGlyph } from '../discGlyphComposite';
import { polygonCentroid } from './polygonCentroid';

const REFRESH_DEBOUNCE_MS = 500;
/** Icon one-swing #564: the evac-order centroid disc size (px). Fixed — an evac ORDER
 *  carries no Response Level, so its disc reads at one prominent protective-action size. */
const EVAC_DISC_SIZE = 26;

export interface OperatorEvacOrderLayerOptions {
  /** Current toggle state — used to set ds.show after a fetch completes. */
  getVisible?: () => boolean;
  /** Reports the rendered order count (for the toggle label / debugging). */
  onCount?: (n: number) => void;
  /** Test seam: inject a fetcher in place of the real API call. */
  fetcher?: (bbox: BboxQuery, signal: AbortSignal) => Promise<OperatorEvacOrderFeatureCollection>;
}

export interface OperatorEvacOrderLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Map a picked Cesium entity back to its operator evac order (null if not ours). */
  pick(entity: unknown): OperatorEvacOrderFeature | null;
  destroy(): void;
}

export function installOperatorEvacuationOrderLayer(
  viewer: Cesium.Viewer,
  opts: OperatorEvacOrderLayerOptions = {},
): OperatorEvacOrderLayerHandle {
  const fetcher = opts.fetcher ?? ((bbox, signal) => fetchOperatorEvacuationOrders(bbox, signal));
  const ds = new Cesium.GeoJsonDataSource('operator-evacuation-orders');
  void viewer.dataSources.add(ds);

  const entityToFeature = new Map<Cesium.Entity, OperatorEvacOrderFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  // Guards against an older in-flight load() resolving after a newer one.
  let loadGen = 0;

  // Regional-zoom render gate: hidden at country zoom, visible regional+.
  const gate = new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M);

  const styleEntities = (fc: OperatorEvacOrderFeatureCollection): void => {
    const byId = new Map<string, OperatorEvacOrderFeature>();
    for (const f of fc.features) byId.set(f.id, f);
    entityToFeature.clear();
    for (const entity of ds.entities.values) {
      const id = readArcgisId(entity);
      const feature = id ? byId.get(id) : undefined;
      if (!feature) continue;
      const style = evacuationStyle(feature.properties.level);
      const color = Cesium.Color.fromCssColorString(style.color);
      if (entity.polygon) {
        entity.polygon.material = new Cesium.ColorMaterialProperty(color.withAlpha(style.alpha));
        // height 0 (not clamp-to-ground) so the outline renders.
        entity.polygon.height = new Cesium.ConstantProperty(0);
        entity.polygon.outline = new Cesium.ConstantProperty(true);
        entity.polygon.outlineColor = new Cesium.ConstantProperty(
          color.withAlpha(style.strokeAlpha),
        );
        entity.polygon.outlineWidth = new Cesium.ConstantProperty(style.outlineWidth);
        entity.polygon.distanceDisplayCondition = new Cesium.ConstantProperty(gate);
      }
      // Icon one-swing #564 (evac missing-glyph fix): the polygon alone rendered NO
      // symbol — an operator-declared evac ORDER now carries the EXACT-ACT `evacuation`
      // DISC at the order's centroid (a solid protective-action-violet disc with the
      // white evacuation glyph knocked out). A billboard ON the polygon entity (its own
      // position is the centroid), so it's pickable → the same order, regional-gated to
      // match the polygon. billboard.color = WHITE (full opacity — the marker is solid;
      // the ambient violet FILL keeps its own low alpha).
      const centroid = polygonCentroid(feature.geometry);
      if (centroid) {
        if (!entity.position) {
          entity.position = new Cesium.ConstantPositionProperty(
            Cesium.Cartesian3.fromDegrees(centroid[0], centroid[1]),
          );
        }
        entity.billboard = new Cesium.BillboardGraphics({
          image: renderDiscGlyph('evacuation', { fill: style.color }) ?? undefined,
          width: EVAC_DISC_SIZE,
          height: EVAC_DISC_SIZE,
          color: Cesium.Color.WHITE,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: gate,
        });
      }
      entityToFeature.set(entity, feature);
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      await ds.load(fc, { clampToGround: false });
      if (destroyed || gen !== loadGen) return;
      styleEntities(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchOperatorEvacuationOrders failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): OperatorEvacOrderFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
